'use client';

import { useSearchParams } from 'next/navigation';
import { useState, useEffect } from 'react';
import Session, {
  useSessionContext,
} from 'supertokens-auth-react/recipe/session';

import { AuthModal } from '@/components/organisms';
import { useRouter } from '@/i18n/routing';
import { useDialog } from '@/lib/hooks/auth/useDialog';
import { useBreakpoint } from '@/lib/hooks/util/useBreakpoint';

import { DesktopNavbar } from './DesktopNavbar';
import { MobileDrawer } from './MobileDrawer';

import type { LoadedSessionContext } from 'supertokens-auth-react/lib/build/recipe/session/types';

import type { Page, Language } from '@/lib/api/types';

interface NavbarProps {
  pages: Page[];
  languages: Language[];
  isCreateUser: boolean;
  logo: string;
  name: string;
}

export const Navigation = ({ languages, pages, logo, name }: NavbarProps) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuToggle = () => setMenuOpen(prevState => !prevState);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const isDesktop = useBreakpoint('lg') as boolean;
  const shouldUseDesktop = isDesktop && !isOverflowing;
  const searchParams = useSearchParams();
  const router = useRouter();
  const { doesSessionExist } = useSessionContext() as LoadedSessionContext;
  const { openDialog, closeDialog } = useDialog();
  const handleLogout = async () => {
    await Session.signOut();
  };
  const loginAction = () => {
    openDialog('auth');
  };

  useEffect(() => {
    if (doesSessionExist) {
      closeDialog();
    }
  }, [closeDialog, doesSessionExist]);

  useEffect(() => {
    if (searchParams.get('show') === 'signin' && !doesSessionExist) {
      openDialog('auth');
      const newParams = new URLSearchParams(searchParams.toString());
      newParams.delete('show');
      router.replace(`?${newParams.toString()}`, { scroll: false });
    }
  }, [searchParams, doesSessionExist, router, openDialog]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 960) setDrawerOpen(false);
      setIsOverflowing(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="[position:var(--navigation-position)] w-full top-0 z-[1200]">
      <DesktopNavbar
        logo={logo}
        name={name}
        isDesktop={shouldUseDesktop}
        menuToggle={menuToggle}
        menuOpen={menuOpen}
        pages={pages}
        languages={languages}
        setDrawerOpen={setDrawerOpen}
        drawerOpen={drawerOpen}
        authenticated={doesSessionExist}
        logoutAction={handleLogout}
        loginAction={loginAction}
        setIsOverflowing={setIsOverflowing}
      />
      {!shouldUseDesktop && (
        <MobileDrawer
          drawerOpen={drawerOpen}
          drawerOpenAction={setDrawerOpen}
          pages={pages}
          languages={languages}
          menuToggle={menuToggle}
          menuOpen={menuOpen}
          authenticated={doesSessionExist}
          logoutAction={handleLogout}
          loginAction={loginAction}
        />
      )}
      <AuthModal name={name} />
    </div>
  );
};
